/**
 * resolveTargets.js — turning a step's targets into live elements.
 *
 * Half the things a tutorial points at are not in the DOM when the step begins:
 * the outline panel mounts only after its CSS transition ends, the error list
 * renders only once there are errors, a node appears only after its op applies,
 * and the whole editor is replaced by "Loading..." until ownership is verified.
 *
 * So resolution waits rather than querying once and giving up.
 */

/**
 * @param {import('./stepSchema').TargetRef} ref
 * @param {object} ctx
 * @returns {string|null} a CSS selector, or null if the ref cannot be resolved yet
 */
export function toSelector(ref, ctx) {
    if (!ref) return null;
    if (typeof ref === 'function') {
        const selector = ref(ctx);
        return typeof selector === 'string' && selector.length > 0 ? selector : null;
    }
    if (typeof ref === 'object') return ref.selector || null;
    return `[data-tutorial="${ref}"]`;
}

/** The padding an explicit `{selector, padding}` ref asks for, else the default. */
export function paddingOf(ref, fallback) {
    return ref && typeof ref === 'object' && typeof ref.padding === 'number' ? ref.padding : fallback;
}

export class TargetTimeoutError extends Error {
    constructor(selectors) {
        super(`Tutorial targets never appeared: ${selectors.join(', ')}`);
        this.name = 'TargetTimeoutError';
        this.selectors = selectors;
    }
}

/**
 * Resolves once EVERY selector matches something, because a step with two holes
 * is wrong until both exist.
 *
 * @param {string[]} selectors
 * @param {{timeoutMs?: number, signal?: AbortSignal, root?: Document}} options
 * @returns {Promise<Array<{selector: string, el: Element}>>}
 */
export function waitForElements(selectors, { timeoutMs = 10000, signal, root } = {}) {
    const doc = root || document;

    return new Promise((resolve, reject) => {
        if (selectors.length === 0) {
            resolve([]);
            return;
        }

        let observer = null;
        let timeoutId = null;
        let frameId = null;
        let settled = false;

        const cleanup = () => {
            if (observer) observer.disconnect();
            if (timeoutId !== null) clearTimeout(timeoutId);
            if (frameId !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameId);
            if (signal) signal.removeEventListener('abort', onAbort);
        };

        const attempt = () => {
            if (settled) return;
            const found = [];
            for (const selector of selectors) {
                let el = null;
                try {
                    el = doc.querySelector(selector);
                } catch (error) {
                    // A malformed selector is a bug in the step, not a wait state.
                    settled = true;
                    cleanup();
                    reject(error);
                    return;
                }
                if (!el) return;
                found.push({ selector, el });
            }
            settled = true;
            cleanup();
            resolve(found);
        };

        function onAbort() {
            if (settled) return;
            settled = true;
            cleanup();
            const error = new Error('aborted');
            error.name = 'AbortError';
            reject(error);
        }

        if (signal) {
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener('abort', onAbort);
        }

        // Try straight away: most steps resolve on the first tick.
        attempt();
        if (settled) return;

        // Coalesce bursts of mutations into one check per frame. React can emit
        // hundreds while a route mounts.
        const schedule = () => {
            if (settled || frameId !== null) return;
            if (typeof requestAnimationFrame !== 'function') {
                attempt();
                return;
            }
            frameId = requestAnimationFrame(() => {
                frameId = null;
                attempt();
            });
        };

        observer = new MutationObserver(schedule);
        observer.observe(doc.body || doc.documentElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-tutorial', 'data-id', 'class', 'style'],
        });

        timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new TargetTimeoutError(selectors));
        }, timeoutMs);
    });
}
